import { createActions, createReducer } from 'reduxsauce';
import reduceReducers from 'reduce-reducers';

/**
 * This function creates redux action creators that automatically prepend searchMode as the first parameter. To use the action
 * creators you must first call the init() function on the returned object with searchMode as parameter. This will initialize the
 * creators with searchMode, so that you don't have to pass it as a parameter every time you call one.
 * For actions that are NOT bound to searchMode set the isBoundToSearchMode property to false (see below for more info).
 *
 * @param actions - object with action definitions. Each action definition contains either an array with parameters or an object in
 *                  the following format: { params: [], isBoundToSearchMode: true|false }
 * @returns {{init: (function(*=): {}), Types}}
 */
export function createSearchModeBoundActions(actions) {
  // helper func to check whether the action definition is an object that contains the isBoundToSearchMode property
  // or a regular array with parameters
  const isObjectDefinition = action => Object.prototype
    .hasOwnProperty
    .call(action, 'isBoundToSearchMode');

  // prepend searchMode parameter to every action's parameter list (unless isBoundToSearchMode is false)
  const actionsWithSearchMode = Object.keys(actions).reduce(
    (res, actionName) => {
      const actionDefinition = actions[actionName];

      res[actionName] = (isObjectDefinition(actionDefinition) && !actionDefinition.isBoundToSearchMode)
        ? actionDefinition.params
        : ['searchMode', ...(isObjectDefinition(actionDefinition) ? actionDefinition.params : actionDefinition)];

      return res;
    },
    {}, // initial value for reduce
  );

  const { Types, Creators } = createActions(actionsWithSearchMode);

  return {
    init: searchMode => Object.keys(Creators).reduce(
      (res, actionName) => {
        const actionDefinition = actions[actionName];

        res[actionName] = (isObjectDefinition(actionDefinition) && !actionDefinition.isBoundToSearchMode)
          ? res[actionName] = (...args) => Creators[actionName](...args)
          : res[actionName] = (...args) => Creators[actionName](searchMode, ...args);

        return res;
      },
      {}, // initial value for reduce
    ),
    Types,
  };
}


/**
 * Creates a reducer with nested subreducers that handle part of its state.
 * @param initialState - top level state (must include the initial state for slices managed by subreducers)
 * @param handlers - handlers for the top level state
 * @param subReducers - an object with subreducer definitions. Same as in redux' combineReducers() function.
 */
export function createNestedReducer(initialState, handlers, subReducers) {
  const keys = Object.keys(subReducers);
  // we need to make sure that subreducers will be called only with their slice of the state. Here we wrap each subreducer
  // with one that takes the toplevel state, calls the wrapped subreducer with only his slice of the state and then merges
  // the result back into the toplevel state.
  const wrappedSubreducers = keys.map(key => (state, action) => state.set(key, subReducers[key](state[key], action)));

  return reduceReducers(
    createReducer(initialState, handlers),
    ...wrappedSubreducers,
  );
}
